import { API } from "@js/api";
import { processContractHistoryParams } from "@js/apps/jobs/apps/offers/components/contracts-history-table/utils";
import type { PaginatedResult } from "@js/types/generic";
import type {
  EmployerOffer,
  FreelancerOffer,
  GetBoilerplateTermsResponse,
} from "@js/types/jobs";

import type {
  AcceptJobOfferExtensionParams,
  AcceptJobOfferParams,
  CreateEmployerOfferPayload,
  CreateEmployerOffersBulkPayload,
  CreateOfferDataReturnData,
  GetOfferHistoryParams,
  OfferHistoryItem,
  RejectJobOfferParams,
  UpdateOfferDataReturnData,
} from "./types";

export const offersApi = API.injectEndpoints({
  endpoints: (build) => ({
    getEmployerOffer: build.query<EmployerOffer, EmployerOffer["id"]>({
      query: (id) => ({
        url: `/employer_offers/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [
        { type: "EmployerOffers", id: arg },
      ],
    }),
    createEmployerOffersBulk: build.mutation<
      void,
      CreateEmployerOffersBulkPayload
    >({
      query: (data) => ({
        url: "/employer_offers/bulk_create",
        method: "POST",
        data,
      }),
      invalidatesTags: [
        { type: "EmployerOffers", id: "LIST" },
        { type: "EmployerBids" },
        "Jobs",
        "BulkOffersPreview",
      ],
    }),
    createEmployerOffer: build.mutation<
      CreateOfferDataReturnData,
      CreateEmployerOfferPayload
    >({
      query: (data) => ({
        url: "/employer_offers/",
        method: "POST",
        data,
      }),
      invalidatesTags: (_result, _error, arg) => {
        if (arg.dry_run) {
          return [];
        }

        return [
          { type: "EmployerOffers", id: "LIST" },
          { type: "EmployerBids" },
          "Jobs",
          "ManagedEmployers",
          "BulkOffersPreview",

          /**
           * No need to invalidate "EmployerCurrentOfferDeposit" while creating an offer,
           * we are listening for a websocket event about deposit status change.
           * We are saving 2 redundant offer deposit get requests (on dry run + on creation)
           */
        ];
      },
    }),
    updateEmployerOffer: build.mutation<
      UpdateOfferDataReturnData,
      { id: EmployerOffer["id"]; data: CreateEmployerOfferPayload }
    >({
      query: ({ id, data }) => ({
        url: `/employer_offers/${id}/`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerOffers", id: arg.id },
        { type: "EmployerBids" },
        { type: "OfferHistory", id: arg.id },
        "Jobs",
        "ManagedEmployers",
        "EmployerCurrentOfferDeposit",
        "EmployerMyTalent",
      ],
    }),
    cancelEmployerOffer: build.mutation<void, EmployerOffer["id"]>({
      query: (id) => ({
        url: `/employer_offers/${id}/cancel_offer/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "EmployerOffers", id: arg },
        { type: "EmployerBids" },
        "Jobs",
        "ManagedEmployers",
        "EmployerCurrentOfferDeposit",
        "BulkOffersPreview",
      ],
    }),
    getFreelancerOffer: build.query<FreelancerOffer, FreelancerOffer["id"]>({
      query: (id) => ({
        url: `/freelancer_offers/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [
        { type: "FreelancerOffers", id: arg },
      ],
    }),
    rejectJobOffer: build.mutation<void, RejectJobOfferParams>({
      query: ({ id, ...data }) => ({
        url: `/freelancer_offers/${id}/reject_offer/`,
        method: "POST",
        data,
      }),
    }),
    acceptJobOffer: build.mutation<void, AcceptJobOfferParams>({
      query: ({ id, ...data }) => ({
        url: `/freelancer_offers/${id}/accept_offer/`,
        method: "POST",
        data,
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // do not update optimistically so the offer page is not rerendered while the accept query is pending
          dispatch(
            offersApi.util.updateQueryData(
              "getFreelancerOffer",
              arg.id,
              (freelancerOffer) => {
                freelancerOffer.status = "accepted";
              },
            ),
          );
        } catch (e) {
          void e;
        }
      },
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [{ type: "FreelancerOffers", id: arg.id }, "FreelancerJobOffers"],
    }),
    acceptJobOfferExtension: build.mutation<
      void,
      AcceptJobOfferExtensionParams
    >({
      query: ({ id, ...data }) => ({
        url: `/freelancer_offers/${id}/accept_offer_extension/`,
        method: "POST",
        data,
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // do not update optimistically so the offer page is not rerendered while the accept query is pending
          dispatch(
            offersApi.util.updateQueryData(
              "getFreelancerOffer",
              arg.id,
              (freelancerOffer) => {
                freelancerOffer.requires_extension_acceptance = false;
              },
            ),
          );
        } catch (e) {
          void e;
        }
      },
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [{ type: "FreelancerOffers", id: arg.id }, "FreelancerJobOffers"],
    }),
    getEmployerOfferHistory: build.query<
      PaginatedResult<OfferHistoryItem>,
      GetOfferHistoryParams
    >({
      query: ({ id, filters }) => {
        const params = processContractHistoryParams(filters);

        return {
          url: `/employer_offers/${id}/history`,
          method: "GET",
          params,
        };
      },
      providesTags: (_res, _err, arg) => [{ type: "OfferHistory", id: arg.id }],
    }),
    getFreelancerOfferHistory: build.query<
      PaginatedResult<OfferHistoryItem>,
      GetOfferHistoryParams
    >({
      query: ({ id, filters }) => {
        const params = processContractHistoryParams(filters);

        return {
          url: `/freelancer_offers/${id}/history`,
          method: "GET",
          params,
        };
      },
      providesTags: (_res, _err, arg) => [{ type: "OfferHistory", id: arg.id }],
    }),
    getBoilerplateTerms: build.query<GetBoilerplateTermsResponse, void>({
      query: () => ({
        url: `/boilerplate_terms/`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetEmployerOfferQuery,
  useCreateEmployerOfferMutation,
  useUpdateEmployerOfferMutation,
  useCancelEmployerOfferMutation,
  useGetFreelancerOfferQuery,
  useRejectJobOfferMutation,
  useAcceptJobOfferMutation,
  useGetEmployerOfferHistoryQuery,
  useGetFreelancerOfferHistoryQuery,
  useAcceptJobOfferExtensionMutation,
  useGetBoilerplateTermsQuery,
  useCreateEmployerOffersBulkMutation,
} = offersApi;

export const useGetEmployerOfferQueryState =
  offersApi.endpoints.getEmployerOffer.useQueryState;
